import { useLocalStorage } from '@cheqroom/hooks';

import { useAuth } from './../../context/Authentication.context';
import { NamespacedOverviewViewConfig, OverviewConfig, OverviewViewConfig } from './types';

// Categories is deprecated?
export type ViewType = 'table' | 'cards' | 'scheduler' | 'categories';

export type OverviewType =
	| 'items-list'
	| 'check-outs-list'
	| 'contacts-list'
	| 'kits-list'
	| 'reservations-list'
	| 'spotchecks-list'
	| 'tickets-list';

// Type the hook arguments
export type UseOverviewConfigArgs<Field> = {
	view?: ViewType;
	type: OverviewType;
	namespace?: string;
	defaultConfig?: OverviewViewConfig<Field>;
};

// Type the storage that is stored in the localstorage (which can be namespaced and thus is a hashmap)
export type StorageType<Field> = OverviewConfig & (OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field>);

export type ConfigReturnType<Field> = readonly [
	OverviewConfig & OverviewViewConfig<Field>, // Getter
	(config: OverviewConfig & OverviewViewConfig<Field>) => OverviewViewConfig<Field>, // Setter
	() => void, // Clear
];

// Overload for 'items-list' which will have a namespace
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & { type: 'items-list'; namespace: string | 'all' }
): ConfigReturnType<Field>;
// Overload for all other types which will NOT have a namespace
function useOverviewConfig<Field>(
	args: UseOverviewConfigArgs<Field> & {
		type: Exclude<UseOverviewConfigArgs<Field>['type'], 'items-list'>;
	}
): ConfigReturnType<Field>;

// Actual implementation
function useOverviewConfig<Field>({ view, type, namespace, defaultConfig }: UseOverviewConfigArgs<Field>) {
	const defaultOverviewConfig: OverviewConfig = { view: 'table', pageSize: 25 };
	const defaultViewConfig = defaultConfig ?? defaultViewConfigs[type];
	const { id: userId } = useAuth();

	if (type === 'items-list' && !namespace) {
		// This means that the items-list is not namespaced,
		// which can only happen if we've selected the "all items" tab in the sidebar.
		// This view should not be able to be customized, so we'll just use the default view config
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn(`No namespace provided for items-list ${type} - ${view ?? 'NO VIEW'}`);
		}
	}

	// Prefix of the storage key
	// e.g. 000app.kits-list
	const storageKeyPrefix = `${userId!}app.${type}`;

	// Config for the overview (usually the last used view and pagination size)
	const [overviewConfig, setOverviewConfig, clearOverviewConfig] = useLocalStorage<OverviewConfig>(
		`${storageKeyPrefix}.config`,
		defaultOverviewConfig
	);

	// The specific view config for the overview (e.g. the columns/fields to show)
	// The content of the config CAN be a hashmap where the key is the namespace
	let initialViewConfig: OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field> | undefined =
		defaultViewConfigs[type];
	if (defaultViewConfig) {
		if (namespace) {
			initialViewConfig = {
				[namespace]: defaultViewConfig,
			};
		} else {
			initialViewConfig = defaultViewConfig;
		}
	} else {
		// This will probably trigger a hook count error, but this is a developer error so we want to know about it
		throw new Error(`No default view config found for type ${type}`);
	}

	const [_overviewViewConfig, setOverviewViewConfig, clearOverviewViewConfig] = useLocalStorage<
		OverviewViewConfig<Field> | NamespacedOverviewViewConfig<Field>
	>(`${storageKeyPrefix}.${view ?? overviewConfig?.view ?? defaultOverviewConfig.view}`, initialViewConfig);

	// Use type guards to get the correct type
	const overviewViewConfig = namespace
		? (_overviewViewConfig as NamespacedOverviewViewConfig<Field>)
		: (_overviewViewConfig as OverviewViewConfig<Field>);

	// For items-list, set the config for a specific namespace
	const handleSetOverviewConfig = (config: OverviewConfig & OverviewViewConfig<Field>) => {
		const { view, pageSize, ...rest } = config;
		// Set the view config, this is not namespaced
		setOverviewConfig({
			view,
			pageSize,
		});

		if (namespace) {
			// Namespaced setter
			setOverviewViewConfig({
				...overviewViewConfig,
				[namespace]: rest,
			});
		} else {
			// Plain setter
			setOverviewViewConfig(rest as OverviewViewConfig<Field>);
		}
	};

	// Clear the config from the localstorage
	const handleClearOverviewConfig = () => {
		clearOverviewConfig();
		clearOverviewViewConfig();
	};

	if (namespace) {
		return [
			{
				...(overviewConfig ?? defaultOverviewConfig),
				...(overviewViewConfig
					? ((overviewViewConfig as NamespacedOverviewViewConfig<Field>)[namespace] ?? defaultViewConfig)
					: defaultViewConfig),
			} as StorageType<Field>,
			handleSetOverviewConfig,
			handleClearOverviewConfig,
		] as const;
	} else {
		return [
			{
				...(overviewConfig ?? defaultOverviewConfig),
				...overviewViewConfig,
			} as StorageType<Field>,
			handleSetOverviewConfig,
			handleClearOverviewConfig,
		] as const;
	}
}

/**
 * Centralized default configs for all overview types
 * Can be overridden by the defaultConfig prop in the useOverviewConfig hook
 */
const defaultViewConfigs: Record<OverviewType, OverviewViewConfig<any>> = {
	'items-list': {
		fields: ['name', 'category', 'location', 'brand', 'model'],
		inlineFields: [],
	},
	'check-outs-list': {
		fields: ['name', 'items', 'from', 'to', 'contact', 'location', 'duration', 'created'],
		inlineFields: [],
	},
	'contacts-list': {
		fields: ['name', 'email', 'access', 'user.userRole.name', 'contactGroups.name'],
		inlineFields: [],
	},
	'kits-list': {
		fields: ['name', 'description', 'items'],
		inlineFields: [],
	},
	'reservations-list': {
		fields: ['name', 'items', 'from', 'to', 'contact', 'location', 'duration', 'created'],
		inlineFields: [],
	},
	'tickets-list': {
		fields: [
			'name',
			'assignee',
			'location',
			'label',
			'description',
			'reporter',
			'created',
			'cancelled',
			'closed',
			'started',
			'modified',
			'archived',
		],
		inlineFields: [],
	},
	'spotchecks-list': {
		fields: ['name', 'created', 'closed', 'progress', 'participants'],
		inlineFields: [],
	},
};

export const getDefaultViewConfigWithInlineFields = <Field>(type: OverviewType, inlineFields: Field[]) => {
	return { ...defaultViewConfigs[type], inlineFields } as OverviewViewConfig<Field>;
};

export default useOverviewConfig;
